/**
 * Client-side cutlist optimiser for rod/bar/tube products (round, square, or
 * triangular cross-section). Unlike the sheet tool this is a 1D cutting-stock
 * problem: how many pieces of a given length fit along a length of stock,
 * accounting for kerf between cuts. Cross-section shape/size never affects
 * that packing - it only affects the machining charge (see
 * assets/rod-machining-charge.js).
 *
 * Depends on:
 *  - window.RodCutlistProductData (rendered by snippets/product-rod-cutlist-optimiser.liquid):
 *      { variants: [{id, price, length, crossSection}], selectedVariantId, machiningPricing, kerf, labels }
 *  - window.MachiningChargeTable (rendered by snippets/machining-charge-table.liquid)
 *  - window.RodMachiningCharge (assets/rod-machining-charge.js)
 */
(function () {
  'use strict';

  var BADGE_COLORS = ['#16A34A', '#2563EB', '#F59E0B', '#DC2626', '#7C3AED', '#0891B2', '#DB2777', '#65A30D'];

  function badgeForIndex(index) {
    var letter = String.fromCharCode(65 + (index % 26));
    if (index >= 26) letter += Math.floor(index / 26);
    return { letter: letter, color: BADGE_COLORS[index % BADGE_COLORS.length] };
  }

  // ---------------------------------------------------------------------
  // 1D cutting-stock packer (first-fit decreasing).
  // ---------------------------------------------------------------------

  /**
   * Packs a list of pieces ({length, qty, badge}) into as many lengths of
   * `stockLength` as needed. `kerf` (mm) is reserved between neighbouring
   * pieces on the same stock length (never trailing past the last piece, so
   * a piece sized to exactly fill the remaining stock is never rejected for
   * "needing" clearance past the stock's own end).
   *
   * Any leftover length after the last piece is still separated from it by
   * one more cut (unless the piece already reaches the stock's own end), so
   * that cut's kerf comes out of the reported offcut too - a 2000mm stock
   * with one 200mm piece cut off leaves a 1795mm offcut with a 5mm kerf,
   * not 1800mm.
   *
   * Returns { stocks: [{length, pieces:[{start,length,badge}], usedLength, offcutStart, offcutLength}] }
   * or { error: 'piece-too-long', piece } if something can't fit at all.
   */
  function packLengths(pieces, stockLength, kerf) {
    kerf = kerf || 0;

    var units = [];
    for (var i = 0; i < pieces.length; i++) {
      var piece = pieces[i];
      for (var q = 0; q < piece.qty; q++) {
        units.push({ length: piece.length, badge: piece.badge });
      }
    }

    units.sort(function (a, b) {
      return b.length - a.length;
    });

    var stocks = [];

    for (var u = 0; u < units.length; u++) {
      var unit = units[u];

      if (unit.length > stockLength + 1e-9) {
        return { error: 'piece-too-long', piece: unit };
      }

      var target = null;
      for (var s = 0; s < stocks.length; s++) {
        var stock = stocks[s];
        var extra = stock.pieces.length > 0 ? kerf : 0;
        if (stock.usedLength + extra + unit.length <= stockLength + 1e-9) {
          target = stock;
          break;
        }
      }

      if (!target) {
        target = { length: stockLength, usedLength: 0, pieces: [] };
        stocks.push(target);
      }

      var start = target.usedLength + (target.pieces.length > 0 ? kerf : 0);
      target.pieces.push({ start: start, length: unit.length, badge: unit.badge });
      target.usedLength = start + unit.length;
    }

    for (var t = 0; t < stocks.length; t++) {
      var s2 = stocks[t];
      var rawRemainder = s2.length - s2.usedLength;
      var kerfLoss = rawRemainder > 1e-9 ? Math.min(kerf, rawRemainder) : 0;
      s2.offcutStart = s2.usedLength + kerfLoss;
      s2.offcutLength = rawRemainder - kerfLoss;
    }

    return { stocks: stocks };
  }

  // ---------------------------------------------------------------------
  // SVG rendering
  // ---------------------------------------------------------------------

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var SVG_MAX_PX = 360;
  var BAR_HEIGHT_PX = 56;
  var OFFCUT_MIN_LENGTH_RATIO = 0.03;

  function renderStockSvg(stock, offcutLabel) {
    var scale = SVG_MAX_PX / stock.length;
    var pxW = stock.length * scale;
    var pxH = BAR_HEIGHT_PX;

    var svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'rodcutlist__stock-plan');
    svg.setAttribute('viewBox', '0 0 ' + pxW + ' ' + pxH);
    svg.setAttribute('width', pxW);
    svg.setAttribute('height', pxH);

    for (var i = 0; i < stock.pieces.length; i++) {
      var p = stock.pieces[i];

      var rect = document.createElementNS(SVG_NS, 'rect');
      rect.setAttribute('x', p.start * scale);
      rect.setAttribute('y', 0);
      rect.setAttribute('width', p.length * scale);
      rect.setAttribute('height', pxH);
      rect.setAttribute('rx', 4);
      rect.setAttribute('fill', p.badge.color);
      svg.appendChild(rect);

      var label = document.createElementNS(SVG_NS, 'text');
      label.setAttribute('class', 'rodcutlist__piece-label');
      label.setAttribute('x', (p.start + p.length / 2) * scale);
      label.setAttribute('y', pxH / 2);
      label.textContent = p.badge.letter + ' ' + Math.round(p.length);
      svg.appendChild(label);
    }

    if (stock.offcutLength >= stock.length * OFFCUT_MIN_LENGTH_RATIO) {
      var offcutRect = document.createElementNS(SVG_NS, 'rect');
      offcutRect.setAttribute('class', 'rodcutlist__offcut-rect');
      offcutRect.setAttribute('x', stock.offcutStart * scale + 1);
      offcutRect.setAttribute('y', 1);
      offcutRect.setAttribute('width', Math.max(stock.offcutLength * scale - 2, 0));
      offcutRect.setAttribute('height', pxH - 2);
      offcutRect.setAttribute('rx', 4);
      svg.appendChild(offcutRect);

      var offcutText = document.createElementNS(SVG_NS, 'text');
      offcutText.setAttribute('class', 'rodcutlist__offcut-label');
      offcutText.setAttribute('x', (stock.offcutStart + stock.offcutLength / 2) * scale);
      offcutText.setAttribute('y', pxH / 2);
      offcutText.textContent = offcutLabel + ' ' + Math.round(stock.offcutLength);
      svg.appendChild(offcutText);
    }

    return svg;
  }

  function stockUsedPercent(stock) {
    return Math.round((stock.usedLength / stock.length) * 100);
  }

  // ---------------------------------------------------------------------
  // UI wiring
  // ---------------------------------------------------------------------

  function init() {
    var root = document.querySelector('[data-cutlist-root]');
    if (!root || !window.RodCutlistProductData) return;

    var data = window.RodCutlistProductData;
    var labels = data.labels || {};
    var variantsById = {};
    for (var i = 0; i < data.variants.length; i++) variantsById[data.variants[i].id] = data.variants[i];

    var currentVariantId = data.selectedVariantId;
    var lastResult = null;
    var recalcTimer = null;

    document.addEventListener('variant:changed', function (event) {
      if (event.detail && event.detail.variant) {
        currentVariantId = event.detail.variant.id;
        scheduleRecalculate();
      }
    });

    var termsEl = root.querySelector('[data-cutlist-terms]');
    var mainEl = root.querySelector('[data-cutlist-main]');
    var termsAcceptButton = root.querySelector('[data-cutlist-terms-accept]');

    function showTerms() {
      if (termsEl) termsEl.hidden = false;
      if (mainEl) mainEl.hidden = true;
    }

    function acceptTerms() {
      if (termsEl) termsEl.hidden = true;
      if (mainEl) mainEl.hidden = false;
    }

    if (termsAcceptButton) {
      termsAcceptButton.addEventListener('click', acceptTerms);
    }

    var toggleButton = document.querySelector('[data-cutlist-toggle]');
    if (toggleButton) {
      toggleButton.addEventListener('click', function () {
        var isHidden = root.hasAttribute('hidden');
        if (isHidden) {
          root.removeAttribute('hidden');
          toggleButton.setAttribute('aria-expanded', 'true');
          showTerms();
        } else {
          root.setAttribute('hidden', '');
          toggleButton.setAttribute('aria-expanded', 'false');
        }
      });
    }

    var rowsBody = root.querySelector('[data-cutlist-rows]');
    var rowTemplate = document.querySelector('[data-cutlist-row-template]');
    var addRowButton = root.querySelector('[data-cutlist-add-row]');
    var addToCartButton = root.querySelector('[data-cutlist-add-to-cart]');
    var checkboxEl = root.querySelector('[data-cutlist-checkbox]');
    var checkboxLabelEl = root.querySelector('[data-cutlist-checkbox-label]');
    var instructionsCheckboxEl = root.querySelector('[data-cutlist-instructions-checkbox]');
    var instructionsInputEl = root.querySelector('[data-cutlist-instructions-input]');
    var errorEl = root.querySelector('[data-cutlist-error]');
    var resultsEl = root.querySelector('[data-cutlist-results]');
    var stocksEl = root.querySelector('[data-cutlist-stocks]');
    var summaryCutLengthEl = root.querySelector('[data-cutlist-summary-cutlength]');
    var summaryMachiningEl = root.querySelector('[data-cutlist-summary-machining]');
    var summaryTotalEl = root.querySelector('[data-cutlist-summary-total]');

    function addRow() {
      var fragment = rowTemplate.content.cloneNode(true);
      rowsBody.appendChild(fragment);
      refreshBadges();
      scheduleRecalculate();
    }

    function removeRow(button) {
      var row = button.closest('.rodcutlist__row');
      if (row) row.remove();
      refreshBadges();
      scheduleRecalculate();
    }

    function refreshBadges() {
      var rows = rowsBody.querySelectorAll('.rodcutlist__row');
      for (var i = 0; i < rows.length; i++) {
        var badge = badgeForIndex(i);
        var badgeEl = rows[i].querySelector('[data-cutlist-badge]');
        badgeEl.textContent = badge.letter;
        badgeEl.style.background = badge.color;
      }
    }

    function readRows() {
      var rows = rowsBody.querySelectorAll('.rodcutlist__row');
      var pieces = [];
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var length = parseFloat(row.querySelector('[data-cutlist-field="length"]').value);
        var qty = parseInt(row.querySelector('[data-cutlist-field="qty"]').value, 10);

        if (!(length > 0) || !(qty > 0)) continue;
        pieces.push({ length: length, qty: qty, badge: badgeForIndex(i) });
      }
      return pieces;
    }

    /**
     * Shows/hides the "Minimum Cut" message directly under each row's
     * length input, live as the customer types - a field only counts as
     * invalid once it has a real entered value below data.minCut, not
     * while it's still blank. Returns true if any row is currently invalid,
     * so calculate() can hold off packing/results until it's fixed.
     */
    function validateMinCut() {
      var rows = rowsBody.querySelectorAll('.rodcutlist__row');
      var message = (labels.minCutMessage || 'Minimum Cut {min}mm').replace('{min}', data.minCut);
      var hasInvalid = false;

      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var length = parseFloat(row.querySelector('[data-cutlist-field="length"]').value);
        var lengthError = row.querySelector('[data-cutlist-field-error="length"]');

        var lengthInvalid = length > 0 && length < data.minCut;

        lengthError.hidden = !lengthInvalid;
        lengthError.textContent = lengthInvalid ? message : '';

        if (lengthInvalid) hasInvalid = true;
      }

      return hasInvalid;
    }

    function showError(message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      resultsEl.hidden = true;
      stocksEl.innerHTML = '';
    }

    function clearError() {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    function formatGBP(amount) {
      return '£' + amount.toFixed(2);
    }

    function scheduleRecalculate() {
      lastResult = null;
      addToCartButton.disabled = true;
      clearTimeout(recalcTimer);
      recalcTimer = setTimeout(calculate, 250);
    }

    function totalPieceCount(pieces) {
      var total = 0;
      for (var i = 0; i < pieces.length; i++) total += pieces[i].qty;
      return total;
    }

    function calculate() {
      clearError();

      var hasInvalidCuts = validateMinCut();
      addRowButton.disabled = hasInvalidCuts;

      var pieces = readRows();
      if (pieces.length === 0 || hasInvalidCuts) {
        stocksEl.innerHTML = '';
        resultsEl.hidden = true;
        return;
      }

      var variant = variantsById[currentVariantId];
      if (!variant) {
        showError('Please select a valid variant before calculating a cut list.');
        return;
      }

      var packResult = packLengths(pieces, variant.length, data.kerf);
      if (packResult.error === 'piece-too-long') {
        var p = packResult.piece;
        showError('A part (' + Math.round(p.length) + 'mm) is longer than the stock length (' + variant.length + 'mm).');
        return;
      }

      var machiningResult = window.RodMachiningCharge.computeRodMachiningCharge(
        variant.crossSection,
        totalPieceCount(pieces),
        data.machiningPricing,
        window.MachiningChargeTable
      );

      if (!machiningResult) {
        showError('Could not calculate the machining charge for this cut list. Please check the sizes entered.');
        return;
      }

      lastResult = {
        variant: variant,
        pieces: pieces,
        stocks: packResult.stocks,
        machiningResult: machiningResult,
      };

      renderResults(lastResult);
      addToCartButton.disabled = false;
    }

    function renderResults(result) {
      stocksEl.innerHTML = '';

      for (var i = 0; i < result.stocks.length; i++) {
        var stock = result.stocks[i];

        var card = document.createElement('div');
        card.className = 'rodcutlist__stock';

        var title = document.createElement('p');
        title.className = 'rodcutlist__stock-title';
        title.innerHTML =
          (labels.stock || 'Stock') + ' ' + (i + 1) + ' <span>· ' + Math.round(stock.length) + 'mm</span>';
        card.appendChild(title);

        var meta = document.createElement('p');
        meta.className = 'rodcutlist__stock-meta';
        meta.innerHTML =
          '<strong>' + stockUsedPercent(stock) + '% ' + (labels.used || 'used') + '</strong> · ' + stock.pieces.length + ' ' + (labels.parts || 'parts');
        card.appendChild(meta);

        card.appendChild(renderStockSvg(stock, labels.offcut || 'Offcut'));
        stocksEl.appendChild(card);
      }

      var machiningGBP = result.machiningResult.chargeGBPRounded;

      summaryCutLengthEl.textContent = 'Total cuts: ' + result.machiningResult.totalPieces;
      summaryMachiningEl.textContent = 'Machining charge: ' + formatGBP(machiningGBP);
      summaryTotalEl.textContent =
        'Total with cuts: ' + formatGBP(result.stocks.length * (result.variant.price / 100) + machiningGBP);

      resultsEl.hidden = false;
    }

    /**
     * The cut list text for one physical length of stock, grouping its
     * placed pieces by badge letter and looking each letter's length back
     * up from the original entered rows (not the placed piece), matching
     * the sheet tool's buildSheetCutlistText.
     */
    function buildStockCutlistText(stock, originalPieces) {
      var counts = {};
      var order = [];
      for (var i = 0; i < stock.pieces.length; i++) {
        var letter = stock.pieces[i].badge.letter;
        if (!(letter in counts)) {
          counts[letter] = 0;
          order.push(letter);
        }
        counts[letter]++;
      }

      return order
        .map(function (letter) {
          var original = originalPieces.filter(function (p) {
            return p.badge.letter === letter;
          })[0];
          return counts[letter] + '× ' + letter + ' ' + Math.round(original.length) + 'mm';
        })
        .join(', ');
    }

    /**
     * The full 'Cut list' cart property text: one "Item X of N: ..." group
     * per physical length of stock, so it's clear which cuts come from
     * which length even though they all share one cart line.
     */
    function buildCutlistSummaryText(stocks, originalPieces) {
      var itemLabel = labels.item || 'Item';
      return stocks
        .map(function (stock, index) {
          return itemLabel + ' ' + (index + 1) + ' of ' + stocks.length + ': ' + buildStockCutlistText(stock, originalPieces);
        })
        .join(' - ');
    }

    function addToCart() {
      if (!lastResult) {
        showError('Please wait for the cut list to finish calculating before adding it to the cart.');
        return;
      }

      addToCartButton.disabled = true;

      var ref = Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8);
      var stocksRequired = lastResult.stocks.length;

      var checkboxLabel = checkboxLabelEl ? checkboxLabelEl.textContent.trim() : 'Include offcuts?';
      var checkboxValue = checkboxEl && checkboxEl.checked ? 'Yes' : 'No';

      var items = [
        {
          id: lastResult.variant.id,
          quantity: stocksRequired,
          properties: {
            'Cut list': buildCutlistSummaryText(lastResult.stocks, lastResult.pieces),
            _cutlist_layout: JSON.stringify(lastResult.stocks),
            _cutlist_ref: ref,
            // Reuses the sheet tool's 'sheet' role (not 'stock') on purpose:
            // assets/cutlist-cart-link.js and the cart templates key off this
            // exact value to lock/cascade the paired machining line, and that
            // logic is shape-agnostic - reusing it here needs no cart changes.
            _cutlist_role: 'sheet',
            _cutlist_min_qty: String(stocksRequired),
          },
        },
      ];

      if (checkboxLabel) {
        items[0].properties[checkboxLabel] = checkboxValue;
      }

      if (instructionsCheckboxEl && instructionsCheckboxEl.checked && instructionsInputEl) {
        var instructionsValue = instructionsInputEl.value.trim();
        if (instructionsValue) {
          var instructionsLabel = (labels.instructionsProperty || 'Additional instructions').trim();
          items[0].properties[instructionsLabel] = instructionsValue;
        }
      }

      var machiningItems = lastResult.machiningResult.cartItems;
      var totalQuantityAdded = stocksRequired;
      for (var i = 0; i < machiningItems.length; i++) {
        items.push({
          id: machiningItems[i].id,
          quantity: machiningItems[i].quantity,
          properties: { _cutlist_ref: ref, _cutlist_role: 'machining' },
        });
        totalQuantityAdded += machiningItems[i].quantity;
      }

      fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: items }),
      })
        .then(function (response) {
          if (!response.ok) {
            return response.json().then(function (body) {
              throw new Error(body.description || body.message || 'Could not add the cut list to the cart.');
            });
          }
          return response.json();
        })
        .then(function () {
          root.dispatchEvent(
            new CustomEvent('product:added', {
              bubbles: true,
              detail: { quantity: totalQuantityAdded, button: addToCartButton },
            })
          );
        })
        .catch(function (err) {
          showError(err.message);
        })
        .finally(function () {
          addToCartButton.disabled = false;
        });
    }

    addRowButton.addEventListener('click', addRow);
    rowsBody.addEventListener('click', function (event) {
      if (event.target.closest('[data-cutlist-remove-row]')) removeRow(event.target);
    });
    rowsBody.addEventListener('input', scheduleRecalculate);
    addToCartButton.addEventListener('click', addToCart);

    if (instructionsCheckboxEl && instructionsInputEl) {
      instructionsCheckboxEl.addEventListener('change', function () {
        instructionsInputEl.hidden = !instructionsCheckboxEl.checked;
        if (!instructionsCheckboxEl.checked) {
          instructionsInputEl.value = '';
          instructionsInputEl.style.height = '';
        } else {
          resizeInstructionsInput();
        }
      });
      instructionsInputEl.addEventListener('input', resizeInstructionsInput);
    }

    function resizeInstructionsInput() {
      if (!instructionsInputEl) return;
      instructionsInputEl.style.height = 'auto';
      instructionsInputEl.style.height = instructionsInputEl.scrollHeight + 'px';
    }

    // Start with one empty row so the table isn't blank on open.
    addRow();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose the packer for testing.
  window.RodCutlistPacker = { packLengths: packLengths, badgeForIndex: badgeForIndex };
})();
